/**
 * reportExport.service — Genera y entrega archivos tabulares multiplataforma.
 * Depende de librerías de archivo y adaptadores nativos; exporta contratos genéricos, sin conocer módulos de negocio ni UI.
 */
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export type ReportExportFormat = "csv" | "pdf" | "xlsx" | "zip";

export interface ReportExportMetadata {
  businessName: string;
  generatedBy: string;
  generatedAt: string;
  timeZone: string;
}

export interface ReportExportColumn<Row> {
  id: string;
  label: string;
  value: (row: Row) => string | number | null | undefined;
}

export interface ReportExportDefinition<Row> {
  title: string;
  filenameBase: string;
  filters: string[];
  columns: ReportExportColumn<Row>[];
  rows: Row[];
  summary?: Array<{ label: string; value: string | number }>;
  metadata?: ReportExportMetadata;
  zip?: {
    getDocumentTitle: (row: Row) => string;
    getFilenameBase: (row: Row) => string;
  };
}

export interface ReportExportFile {
  blob: Blob;
  filename: string;
  mimeType: string;
}

const REPORT_ROW_LIMITS: Record<ReportExportFormat, number> = {
  csv: 50_000,
  xlsx: 30_000,
  pdf: 5_000,
  zip: 100,
};

const ESTIMATED_BYTES_PER_CELL: Record<ReportExportFormat, number> = {
  csv: 28,
  xlsx: 44,
  pdf: 78,
  zip: 310,
};

export function getReportExportRowLimit(format: ReportExportFormat): number {
  return REPORT_ROW_LIMITS[format];
}

export function estimateReportExportSizeBytes<Row>(
  report: Pick<ReportExportDefinition<Row>, "rows" | "columns" | "summary">,
  format: ReportExportFormat,
): number {
  const minimumBytes = format === "zip" ? 24_000 : 2_000;
  const dataCells =
    Math.max(1, report.rows.length) * Math.max(1, report.columns.length);
  const summaryCells = (report.summary?.length ?? 0) * 2;
  return (
    minimumBytes + (dataCells + summaryCells) * ESTIMATED_BYTES_PER_CELL[format]
  );
}

function textValue(value: string | number | null | undefined): string {
  return value === null || value === undefined
    ? ""
    : String(value).replace(/\s+/g, " ").trim();
}

function protectedCell(value: string | number | null | undefined): string {
  const text = textValue(value);
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
}

function csvCell(value: string | number | null | undefined): string {
  const text = protectedCell(value);
  return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function pdfText(value: string | number | null | undefined): string {
  return textValue(value)
    .replace(/[^\x20-\x7E\u00C0-\u017F]/g, "")
    .trim();
}

function safeFilename(value: string): string {
  const normalized = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);
  return `${normalized || "reporte"}-${new Date().toISOString().slice(0, 10)}`;
}

function metadataRows(
  metadata?: ReportExportMetadata,
): Array<[string, string]> {
  if (!metadata) return [];
  return [
    ["NEGOCIO", metadata.businessName],
    ["GENERADO POR", metadata.generatedBy],
    [
      "GENERADO",
      new Date(metadata.generatedAt).toLocaleString("es-CO", {
        timeZone: metadata.timeZone,
      }),
    ],
    ["ZONA HORARIA", metadata.timeZone],
  ];
}

function buildCsvBlob<Row>(report: ReportExportDefinition<Row>): Blob {
  const rows = [
    `${csvCell("REPORTE")};${csvCell(report.title)}`,
    ...metadataRows(report.metadata).map(
      ([label, value]) => `${csvCell(label)};${csvCell(value)}`,
    ),
    `${csvCell("REGISTROS")};${report.rows.length}`,
    ...report.filters.map(
      (filter) => `${csvCell("FILTRO")};${csvCell(filter)}`,
    ),
    ...(report.summary ?? []).map(
      ({ label, value }) => `${csvCell(label)};${csvCell(value)}`,
    ),
    "",
    report.columns.map((column) => csvCell(column.label)).join(";"),
    ...report.rows.map((row) =>
      report.columns.map((column) => csvCell(column.value(row))).join(";"),
    ),
  ];
  return new Blob(["\uFEFF", rows.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
}

async function buildXlsxBlob<Row>(
  report: ReportExportDefinition<Row>,
): Promise<Blob> {
  const XLSX = await import("xlsx");
  const reportRows: Array<Array<string | number>> = [
    ["REPORTE", report.title],
    ...metadataRows(report.metadata).map(([label, value]) => [
      label,
      protectedCell(value),
    ]),
    ["REGISTROS", report.rows.length],
    ...report.filters.map((filter) => ["FILTRO", protectedCell(filter)]),
    ...(report.summary ?? []).map(({ label, value }) => [
      protectedCell(label),
      typeof value === "number" ? value : protectedCell(value),
    ]),
    [],
    report.columns.map((column) => protectedCell(column.label)),
    ...report.rows.map((row) =>
      report.columns.map((column) => {
        const value = column.value(row);
        return typeof value === "number" ? value : protectedCell(value);
      }),
    ),
  ];
  const filtersRows: Array<Array<string | number>> = [
    ["REPORTE", report.title],
    ...metadataRows(report.metadata).map(([label, value]) => [
      label,
      protectedCell(value),
    ]),
    ["REGISTROS", report.rows.length],
    [],
    ["FILTROS APLICADOS"],
    ...(report.filters.length
      ? report.filters.map((filter) => [protectedCell(filter)])
      : [["Sin filtros adicionales"]]),
  ];
  const workbook = XLSX.utils.book_new();
  const reportSheet = XLSX.utils.aoa_to_sheet(reportRows);
  const filtersSheet = XLSX.utils.aoa_to_sheet(filtersRows);
  reportSheet["!cols"] = report.columns.map(() => ({ wch: 22 }));
  filtersSheet["!cols"] = [{ wch: 64 }, { wch: 32 }];
  XLSX.utils.book_append_sheet(workbook, reportSheet, "Reporte");
  XLSX.utils.book_append_sheet(workbook, filtersSheet, "Filtros");
  return new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

async function buildPdfBlob<Row>(
  report: ReportExportDefinition<Row>,
): Promise<Blob> {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const document = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const filters = report.filters.length
    ? report.filters.map(pdfText)
    : ["Sin filtros adicionales"];
  const metadata = metadataRows(report.metadata);
  document.setFont("helvetica", "bold");
  document.setFontSize(16);
  document.text(pdfText(report.title), 14, 15);
  document.setFont("helvetica", "normal");
  document.setFontSize(9);
  metadata.forEach(([label, value], index) =>
    document.text(`${pdfText(label)}: ${pdfText(value)}`, 14, 22 + index * 4),
  );
  const filtersStart = 22 + metadata.length * 4;
  document.text(`Registros incluidos: ${report.rows.length}`, 14, filtersStart);
  document.text("Filtros aplicados:", 14, filtersStart + 6);
  filters.forEach((filter, index) =>
    document.text(`- ${filter}`, 18, filtersStart + 11 + index * 4),
  );
  const summaryStart = filtersStart + 13 + filters.length * 4;
  if (report.summary?.length) {
    autoTable(document, {
      startY: summaryStart,
      body: report.summary.map(({ label, value }) => [
        pdfText(label),
        pdfText(value),
      ]),
      theme: "plain",
      styles: { font: "helvetica", fontSize: 8, cellPadding: 1 },
    });
  }
  const lastTable = document as unknown as {
    lastAutoTable?: { finalY?: number };
  };
  autoTable(document, {
    startY: Math.max(
      summaryStart + 7,
      (lastTable.lastAutoTable?.finalY ?? 0) + 6,
    ),
    head: [report.columns.map((column) => pdfText(column.label))],
    body: report.rows.map((row) =>
      report.columns.map((column) => pdfText(column.value(row))),
    ),
    theme: "grid",
    headStyles: { fillColor: [30, 64, 175] },
    styles: { font: "helvetica", fontSize: 7, cellPadding: 1.4 },
  });
  return document.output("blob") as Blob;
}

async function buildZipBlob<Row>(
  report: ReportExportDefinition<Row>,
): Promise<Blob> {
  if (!report.zip)
    throw new Error("La exportación ZIP requiere una plantilla de documento.");
  const { default: JSZip } = await import("jszip");
  const archive = new JSZip();
  await Promise.all(
    report.rows.map(async (row) => {
      const documentReport: ReportExportDefinition<Row> = {
        ...report,
        title: report.zip?.getDocumentTitle(row) ?? report.title,
        rows: [row],
        summary: undefined,
      };
      archive.file(
        `${safeFilename(report.zip?.getFilenameBase(row) ?? report.filenameBase)}.pdf`,
        await buildPdfBlob(documentReport),
      );
    }),
  );
  return archive.generateAsync({ type: "blob" });
}

export async function createReportExport<Row>(
  report: ReportExportDefinition<Row>,
  format: ReportExportFormat,
): Promise<ReportExportFile> {
  const filename = `${safeFilename(report.filenameBase)}.${format}`;
  if (format === "csv")
    return {
      blob: buildCsvBlob(report),
      filename,
      mimeType: "text/csv;charset=utf-8",
    };
  if (format === "xlsx") {
    return {
      blob: await buildXlsxBlob(report),
      filename,
      mimeType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
  }
  if (format === "zip")
    return {
      blob: await buildZipBlob(report),
      filename,
      mimeType: "application/zip",
    };
  return {
    blob: await buildPdfBlob(report),
    filename,
    mimeType: "application/pdf",
  };
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      typeof result === "string"
        ? resolve(result.slice(result.indexOf(",") + 1))
        : reject(new Error("No se pudo leer el archivo."));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function downloadInBrowser(file: ReportExportFile): boolean {
  try {
    const url = URL.createObjectURL(file.blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.filename;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

export async function downloadReportExport(
  file: ReportExportFile,
): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return downloadInBrowser(file);
  try {
    const data = await blobToBase64(file.blob);
    try {
      await Filesystem.writeFile({
        path: file.filename,
        data,
        directory: Directory.Documents,
      });
    } catch {
      await Filesystem.writeFile({
        path: file.filename,
        data,
        directory: Directory.Cache,
      });
    }
    return true;
  } catch {
    return false;
  }
}

export async function shareReportExport(
  file: ReportExportFile,
  title: string,
): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const written = await Filesystem.writeFile({
        path: file.filename,
        data: await blobToBase64(file.blob),
        directory: Directory.Cache,
      });
      await Share.share({ title, url: written.uri, dialogTitle: title });
      return true;
    } catch {
      return false;
    }
  }
  try {
    const nativeFile = new File([file.blob], file.filename, {
      type: file.mimeType,
    });
    if (
      "share" in navigator &&
      "canShare" in navigator &&
      navigator.canShare({ files: [nativeFile] })
    ) {
      await navigator.share({ files: [nativeFile], title });
      return true;
    }
  } catch {
    return false;
  }
  return downloadInBrowser(file);
}
